import { create } from 'zustand';
import { createLocation, getLocation, getLocations, updateLocation } from '@libs/api/locations';
import { Location } from '@libs/models/locations';

type State = {
  locationsInitiated: boolean;
  locations: Location[];
  loading: boolean;
  total: number;
  loadLocations: () => Promise<void>;

  currentWorkingLocationId: string;
  currentWorkingLocationDetailsLoading: boolean;
  currentWorkingLocationDetails: Location | null;
  loadCurrentWorkingLocation: (locationId: string) => void;

  loadLocation: (locationId: string) => void;
  editLocationLoading: boolean;
  editLocationDetails: Location | null;
  flushEdit: () => void;

  createLocation: (location: Omit<Location, 'id'>) => Promise<void>;
  updateLocation: (id: string, location: Location) => Promise<void>;
};
const LS_CURRENT_LOCATION_KEY = 'locationSelection';

export const useLocationStore = create<State>()((set) => ({
  locationsInitiated: false,
  locations: [],
  loading: false,
  total: 0,

  loadLocations: async () => {
    const currentLocID = localStorage.getItem(LS_CURRENT_LOCATION_KEY);
    try {
      set({ loading: true });
      const { data } = await getLocations({
        size: 100,
        page: 1,
      });

      set({
        locations: data,
        currentWorkingLocationId: currentLocID ?? data[0].id,
        total: data.length,
      });
    } finally {
      set({ loading: false, locationsInitiated: true });
    }
  },
  loadLocation: async (locationId: string) => {
    set({ editLocationLoading: true });
    try {
      const resp = await getLocation(locationId);
      set({
        editLocationDetails: resp.data,
      });
    } finally {
      set({ editLocationLoading: false });
    }
  },
  currentWorkingLocationId: '',
  currentWorkingLocationDetailsLoading: false,
  currentWorkingLocationDetails: null,
  setCurrentWorkingLocation: (locationId: string) => {
    set({ currentWorkingLocationId: locationId });
  },
  loadCurrentWorkingLocation: async (locationId: string) => {
    if (!locationId) {
      return;
    }
    set({ currentWorkingLocationDetailsLoading: true });
    localStorage.setItem(LS_CURRENT_LOCATION_KEY, locationId);
    try {
      const resp = await getLocation(locationId);
      set({
        currentWorkingLocationDetails: resp.data,
        currentWorkingLocationId: resp.data.id,
      });
    } finally {
      set({ currentWorkingLocationDetailsLoading: false });
    }
  },
  editLocationLoading: false,
  editLocationDetails: null,
  flushEdit: () => {
    set({
      editLocationLoading: false,
      editLocationDetails: null,
    });
  },

  createLocation: async (location) => {
    set({ editLocationLoading: true });
    try {
      await createLocation(location);
    } finally {
      set({ editLocationLoading: false });
    }
  },

  updateLocation: async (id, location) => {
    set({ editLocationLoading: true });
    try {
      await updateLocation(id, location);
    } finally {
      set({ editLocationLoading: false });
    }
  },
}));
